import React, { useMemo, useState } from 'react';
import { Typography, useMediaQuery } from '@mui/material';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import * as R from 'ramda';
import { capitalizeSingleWord } from 'utils/helpers/format';
import { useUserRolePermissions } from 'hooks';

// Components
import { MDialog } from 'common';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import Filters from './filters/FiltersConnector';
import MobileFilters from './filters/MobileFiltersConnector';
import MobileNav from './mobile-nav/MobileNavConnector';
import MainPanel from './main-panel/MainPanelConnector';
import ViewDrawer from './view-drawer/ViewDrawerConnector';
import EditDrawer from './edit/EditDrawer';
import CustomerAuthorizationsListViewPage from './customer-authorizations-grid/CustomerAuthorizationsListViewPage';

const CustomerAuthorizations = ({
  activateAuthorizations,
  authorizationsToActivate,
  authorizationsToDeactivate,
  confirmDialogType,
  customerAuthorizationToActivate,
  customerAuthorizationToDeactivate,
  customerAuthorizationToDelete,
  deactivateAuthorizations,
  deleteCustomerAuthorizations,
  isActivationDialogOpen,
  isConfirmDialogOpen,
  selectedAuthorizations,
  setActivationDialogOpen,
  setConfirmDialogOpen,
  setConfirmDialogType,
  setCustomerAuthorizationToActivate,
  setCustomerAuthorizationToDelete,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate } = useUserRolePermissions();

  const [customerAuthorizationId, setCustomerAuthorizationId] = useState(null);
  const [refetchToggle, setRefetchToggle] = useState(false);

  const customerAuthGridViewFlag = EvaluateFlag(
    process.env.REACT_APP_LD_CUSTOMER_AUTHORIZATION_GRID_VIEW
  );

  const confirmationDialogMessage = R.not(R.isEmpty(customerAuthorizationToActivate))
    ? `Activate customer loading for ${customerAuthorizationToActivate.name}? Customer
  authorizations can be deactivated at any time.`
    : authorizationsToActivate.length
    ? `Activate customer loading for ${authorizationsToActivate.length} authorizations? Customer
  authorizations can be deactivated at any time.`
    : '';

  const isBulkAction =
    confirmDialogType === 'deactivate'
      ? R.isEmpty(customerAuthorizationToDeactivate)
      : R.isEmpty(customerAuthorizationToDelete);

  const count = isBulkAction
    ? confirmDialogType === 'deactivate'
      ? authorizationsToDeactivate.length || 1
      : selectedAuthorizations.length || 1
    : 'the ';

  const ending = isBulkAction
    ? confirmDialogType === 'deactivate'
      ? authorizationsToDeactivate.length > 1
        ? 's'
        : ''
      : selectedAuthorizations.length > 1
      ? 's'
      : ''
    : '';

  const message = `If you still want to ${confirmDialogType} ${count} authorization${ending}, enter ${confirmDialogType.toUpperCase()} below and click Confirm.`;

  const deactivateWarnings = useMemo(() => {
    const initial = ['Authorizations cannot be reactivated within the same day as deactivation.'];

    return R.isEmpty(customerAuthorizationToDeactivate)
      ? R.concat(
          R.map(
            auth =>
              `This will deactivate the authorization for ${R.defaultTo(
                R.prop('name', auth),
                R.prop('customerName', auth)
              )}`,
            authorizationsToDeactivate
          ),
          initial
        )
      : R.concat(
          [
            `This will deactivate the authorization for ${R.defaultTo(
              R.prop('name', customerAuthorizationToDeactivate),
              R.prop('customerName', customerAuthorizationToDeactivate)
            )}`,
          ],
          initial
        );
  }, [customerAuthorizationToDeactivate, authorizationsToDeactivate]);

  const deleteWarnings = useMemo(() => {
    const initial = [
      'By deleting a customer authorization you will not be able to undo this action!',
    ];
    return R.isEmpty(customerAuthorizationToDelete)
      ? R.concat(
          R.map(
            auth =>
              `This will delete the authorization for ${R.defaultTo(
                R.prop('name', auth),
                R.prop('customerName', auth)
              )}`,
            selectedAuthorizations
          ),
          initial
        )
      : R.concat(
          [
            `This will delete the authorization for ${R.defaultTo(
              R.prop('name', customerAuthorizationToDelete),
              R.prop('customerName', customerAuthorizationToDelete)
            )}`,
          ],
          initial
        );
  }, [customerAuthorizationToDelete, selectedAuthorizations]);

  const warningList = R.cond([
    [R.equals('deactivate'), R.always(deactivateWarnings)],
    [R.equals('delete'), R.always(deleteWarnings)],
  ])(confirmDialogType);

  return (
    <>
      {customerAuthGridViewFlag ? (
        <CustomerAuthorizationsListViewPage />
      ) : (
        <>
          {lgUp ? <Filters /> : <MobileFilters customerAuthorizationId={customerAuthorizationId} />}
          {!lgUp && <MobileNav />}
          <MainPanel
            refetchToggle={refetchToggle}
            setCustomerAuthorizationId={setCustomerAuthorizationId}
          />
          <ViewDrawer setCustomerAuthorizationId={setCustomerAuthorizationId} />
          {R.not(R.isNil(customerAuthorizationId)) && (
            <EditDrawer
              customerAuthorizationId={customerAuthorizationId}
              refetchToggle={refetchToggle}
              setCustomerAuthorizationId={setCustomerAuthorizationId}
              setRefetchToggle={setRefetchToggle}
            />
          )}
          <DialogExplicitConfirmation
            confirmationMessage={message}
            confirmationValue={confirmDialogType.toUpperCase()}
            dialogOpen={isConfirmDialogOpen}
            handleCloseDialog={() => {
              setConfirmDialogOpen(false);
              setTimeout(() => {
                setConfirmDialogType('');
                setCustomerAuthorizationToDelete({});
              }, 600);
            }}
            label={`Type "${confirmDialogType.toUpperCase()}"`}
            onConfirm={() =>
              confirmDialogType === 'delete'
                ? deleteCustomerAuthorizations(currentAffiliate)
                : deactivateAuthorizations(currentAffiliate)
            }
            title={`${capitalizeSingleWord(confirmDialogType)} Authorization?`}
            warningList={warningList}
          />
          <MDialog
            header="Turn on Customer Authorization?"
            leftButtonAction={() => {
              setActivationDialogOpen(false);
              setTimeout(() => {
                setCustomerAuthorizationToActivate({});
              }, 600);
            }}
            leftButtonText="Back"
            onClose={() => {
              setActivationDialogOpen(false);
              setTimeout(() => {
                setCustomerAuthorizationToActivate({});
              }, 600);
            }}
            open={isActivationDialogOpen}
            rightButtonAction={() => activateAuthorizations(currentAffiliate)}
            rightButtonText="Confirm"
          >
            <Typography color="textPrimary" component="span" variant="subtitle1">
              {confirmationDialogMessage}
            </Typography>
          </MDialog>
        </>
      )}
    </>
  );
};

CustomerAuthorizations.propTypes = {
  activateAuthorizations: func.isRequired,
  authorizationsToActivate: arrayOf(shape({})).isRequired,
  authorizationsToDeactivate: arrayOf(shape({})).isRequired,
  confirmDialogType: string.isRequired,
  customerAuthorizationToActivate: shape({}).isRequired,
  customerAuthorizationToDeactivate: shape({}).isRequired,
  customerAuthorizationToDelete: shape({}).isRequired,
  deactivateAuthorizations: func.isRequired,
  deleteCustomerAuthorizations: func.isRequired,
  isActivationDialogOpen: bool.isRequired,
  isConfirmDialogOpen: bool.isRequired,
  selectedAuthorizations: arrayOf(shape({})).isRequired,
  setActivationDialogOpen: func.isRequired,
  setConfirmDialogOpen: func.isRequired,
  setConfirmDialogType: func.isRequired,
  setCustomerAuthorizationToActivate: func.isRequired,
  setCustomerAuthorizationToDelete: func.isRequired,
};

export default CustomerAuthorizations;
